import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import logger from '../utils/logger';
import { 
  User, 
  Bell, 
  Shield, 
  Database, 
  Sliders, 
  Save, 
  Eye, 
  EyeOff,
  Zap,
  Lock,
  Calendar,
  AlertCircle,
  CheckCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeAuthError } from '../utils/authErrors';
import PasswordStrengthMeter from '../components/auth/PasswordStrengthMeter';
import { useRBAC } from '../contexts/RBACContext';
import { useData } from '../contexts/DataContext';
import { getRoleDisplayName, getRoleBadgeColor } from '../config/rbac';
import { SCORE_THRESHOLDS } from '../config/supabase';
import AvailabilitySettings from '../components/settings/AvailabilitySettings';

// Moved outside Settings component to prevent re-creation on every render
const SettingCard = ({ title, description, children }) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
    <div className="mb-4">
      <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      {description && (
        <p className="text-sm text-gray-600 mt-1">{description}</p>
      )}
    </div>
    {children}
  </div>
);

const Toggle = ({ enabled, onChange, label, description }) => (
  <div className="flex items-center justify-between py-3">
    <div>
      <p className="text-sm font-medium text-gray-900">{label}</p>
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
    </div>
    <button
      onClick={() => onChange(!enabled)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        enabled ? 'bg-primary-600' : 'bg-gray-200'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

const Settings = () => {
  const { currentUser, updatePassword } = useAuth();
  const { userRole, isAdminOrHigher } = useRBAC();
  const { userTimezone, updateUserTimezone } = useData();
  const [activeTab, setActiveTab] = useState('profile');
  const [settings, setSettings] = useState({
    // Profile settings
    displayName: '',
    company: '',
    phone: '',
    timezone: userTimezone,
    
    // Notification settings
    emailNotifications: true,
    pushNotifications: true,
    newLeadAlerts: true,
    highScoreAlerts: true,
    dailyDigest: true,
    
    // Scoring settings
    callListThreshold: SCORE_THRESHOLDS.CALL_LIST,
    highPriorityThreshold: SCORE_THRESHOLDS.HIGH_PRIORITY,
    mediumPriorityThreshold: SCORE_THRESHOLDS.MEDIUM_PRIORITY,
    
    // Integration settings
    slackWebhook: '',
    telegramBotToken: '',
    zapierWebhook: '',
    
    // Security settings
    twoFactorEnabled: false,
    sessionTimeout: 30
  });
  const [showApiKeys, setShowApiKeys] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeError, setPasswordChangeError] = useState('');
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);

  const newPasswordValid = useMemo(() => {
    return (
      newPassword.length >= 8 &&
      /[A-Z]/.test(newPassword) &&
      /[a-z]/.test(newPassword) &&
      /[0-9]/.test(newPassword) &&
      /[^A-Za-z0-9]/.test(newPassword)
    );
  }, [newPassword]);

  const newPasswordsMatch = newPassword === confirmNewPassword && confirmNewPassword.length > 0;

  // Check if user signed in with email/password (not OAuth)
  const isEmailUser = currentUser?._supabase?.app_metadata?.provider === 'email';

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordChangeError('');
    setPasswordChangeSuccess(false);

    if (!newPasswordValid) {
      setPasswordChangeError('Please meet all password requirements.');
      return;
    }
    if (!newPasswordsMatch) {
      setPasswordChangeError('Passwords do not match.');
      return;
    }

    try {
      setPasswordChangeLoading(true);
      await updatePassword(newPassword);
      setPasswordChangeSuccess(true);
      setNewPassword('');
      setConfirmNewPassword('');
      setTimeout(() => setPasswordChangeSuccess(false), 5000);
    } catch (err) {
      setPasswordChangeError(sanitizeAuthError(err.message));
      logger.error('Error changing password:', err);
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const allTabs = [
    { id: 'profile', name: 'Profile', icon: User },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'booking', name: 'Booking Calendar', icon: Calendar },
    { id: 'scoring', name: 'Lead Scoring', icon: Sliders },
    { id: 'integrations', name: 'Integrations', icon: Zap },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'data', name: 'Data & Privacy', icon: Database }
  ];

  // Moderator/User roles: only Profile + Security tabs
  // Admin/Developer: all tabs
  const moderatorTabIds = ['profile', 'security'];
  const tabs = isAdminOrHigher()
    ? allTabs
    : allTabs.filter(tab => moderatorTabIds.includes(tab.id));

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    logger.info(`Setting changed: ${key} = ${value}`);
  };

  const handleSave = async () => {
    setIsSaving(true);
    
    try {
      // Update timezone if it changed
      if (settings.timezone !== userTimezone) {
        updateUserTimezone(settings.timezone);
      }
      
      // Simulate API call for other settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsSaving(false);
      logger.info('Settings saved successfully!');
      // Show success message
      alert('Settings saved successfully!');
    } catch (error) {
      logger.error('Error saving settings:', error);
      setIsSaving(false);
      alert('Error saving settings. Please try again.');
    }
  };

  return (
    <div className="py-6 lg:py-8 space-y-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-600 mt-1">Manage your account and application preferences</p>
      </div>

      <div className="flex flex-col lg:flex-row lg:space-x-8">
        {/* Sidebar */}
        <div className="lg:w-64 mb-6 lg:mb-0">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <motion.button
                  key={tab.id}
                  data-tab={tab.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-primary-600' : 'text-gray-500'}`} />
                  <span>{tab.name}</span>
                </motion.button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            {activeTab === 'profile' && (
              <>
                {/* Account Overview Card */}
                <SettingCard
                  title="Account Overview"
                  description="Your account information from Google"
                >
                  <div className="flex items-center space-x-6 p-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl">
                    {/* Google Profile Image */}
                    {currentUser?.photoURL ? (
                      <img 
                        src={currentUser.photoURL} 
                        alt="Profile" 
                        className="w-20 h-20 rounded-full object-cover shadow-lg ring-4 ring-white"
                      />
                    ) : (
                      <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-2xl font-semibold">
                          {currentUser?.email?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900">
                        {currentUser?.displayName || 'No display name set'}
                      </h3>
                      <p className="text-gray-600">{currentUser?.email}</p>
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full ${getRoleBadgeColor(userRole)}`}>
                          {getRoleDisplayName(userRole)}
                        </span>
                      </div>
                    </div>
                  </div>
                </SettingCard>

                {/* Profile Settings Card */}
                <SettingCard
                  title="Profile Settings"
                  description="Customize your in-app display name"
                >
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={currentUser?.email || ''}
                        disabled
                        className="input-field bg-gray-50 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">Email is managed by your Google account</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Display Name
                      </label>
                      <input
                        type="text"
                        value={settings.displayName || currentUser?.displayName || ''}
                        onChange={(e) => handleSettingChange('displayName', e.target.value)}
                        className="input-field"
                        placeholder="Your display name"
                      />
                      <p className="text-xs text-gray-500 mt-1">This name will be shown in the app</p>
                    </div>
                  </div>
                </SettingCard>
              </>
            )}

            {activeTab === 'notifications' && (
              <>
                <SettingCard
                  title="Email Notifications"
                  description="Choose what email notifications you'd like to receive"
                >
                  <div className="space-y-1">
                    <Toggle
                      enabled={settings.emailNotifications}
                      onChange={(value) => handleSettingChange('emailNotifications', value)}
                      label="Email Notifications"
                      description="Receive notifications via email"
                    />
                    <Toggle
                      enabled={settings.newLeadAlerts}
                      onChange={(value) => handleSettingChange('newLeadAlerts', value)}
                      label="New Lead Alerts"
                      description="Get notified when new leads are added"
                    />
                    <Toggle
                      enabled={settings.highScoreAlerts}
                      onChange={(value) => handleSettingChange('highScoreAlerts', value)}
                      label="High Score Alerts"
                      description="Get notified when leads reach call list threshold"
                    />
                    <Toggle
                      enabled={settings.dailyDigest}
                      onChange={(value) => handleSettingChange('dailyDigest', value)}
                      label="Daily Digest"
                      description="Receive a daily summary of your CRM activity"
                    />
                  </div>
                </SettingCard>

                <SettingCard
                  title="Push Notifications"
                  description="Manage browser and mobile push notifications"
                >
                  <Toggle
                    enabled={settings.pushNotifications}
                    onChange={(value) => handleSettingChange('pushNotifications', value)}
                    label="Push Notifications"
                    description="Receive real-time push notifications"
                  />
                </SettingCard>
              </>
            )}

            {activeTab === 'booking' && (
              <AvailabilitySettings />
            )}

            {activeTab === 'scoring' && (
              <>
                <SettingCard
                  title="Lead Scoring Thresholds"
                  description="Configure when leads should be moved to different categories"
                >
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Call List Threshold
                      </label>
                      <input
                        type="number"
                        value={settings.callListThreshold}
                        onChange={(e) => handleSettingChange('callListThreshold', parseInt(e.target.value))}
                        className="input-field"
                        min="1"
                        max="100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leads with this score or higher will appear in the call list
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        High Priority Threshold
                      </label>
                      <input
                        type="number"
                        value={settings.highPriorityThreshold}
                        onChange={(e) => handleSettingChange('highPriorityThreshold', parseInt(e.target.value))}
                        className="input-field"
                        min="1"
                        max="100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leads with this score will be marked as high priority
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Medium Priority Threshold
                      </label>
                      <input
                        type="number"
                        value={settings.mediumPriorityThreshold}
                        onChange={(e) => handleSettingChange('mediumPriorityThreshold', parseInt(e.target.value))}
                        className="input-field"
                        min="1"
                        max="100"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Leads with this score will be marked as medium priority
                      </p>
                    </div>
                  </div>
                </SettingCard>
              </>
            )}

            {activeTab === 'integrations' && (
              <>
                <SettingCard
                  title="Slack Integration"
                  description="Receive CRM notifications in your Slack workspace"
                >
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Slack Webhook URL
                      </label>
                      <input
                        type="url"
                        value={settings.slackWebhook}
                        onChange={(e) => handleSettingChange('slackWebhook', e.target.value)}
                        className="input-field"
                        placeholder="https://hooks.slack.com/services/..."
                      />
                    </div>
                    <button className="btn-secondary text-sm">
                      Test Slack Connection
                    </button>
                  </div>
                </SettingCard>

                <SettingCard
                  title="Telegram Integration"
                  description="Get instant notifications via Telegram bot"
                >
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Bot Token
                      </label>
                      <input
                        type="text"
                        value={settings.telegramBotToken}
                        onChange={(e) => handleSettingChange('telegramBotToken', e.target.value)}
                        className="input-field"
                        placeholder="Your Telegram bot token"
                      />
                    </div>
                    <button className="btn-secondary text-sm">
                      Test Telegram Connection
                    </button>
                  </div>
                </SettingCard>

                <SettingCard
                  title="Zapier Integration"
                  description="Connect your CRM to thousands of apps via Zapier"
                >
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Zapier Webhook URL
                      </label>
                      <input
                        type="url"
                        value={settings.zapierWebhook}
                        onChange={(e) => handleSettingChange('zapierWebhook', e.target.value)}
                        className="input-field"
                        placeholder="https://hooks.zapier.com/hooks/catch/..."
                      />
                    </div>
                    <button className="btn-secondary text-sm">
                      Test Zapier Connection
                    </button>
                  </div>
                </SettingCard>
              </>
            )}

            {activeTab === 'security' && (
              <>
                {/* Change Password - only for email/password users */}
                {isEmailUser && (
                  <SettingCard
                    title="Change Password"
                    description="Update your account password"
                  >
                    {passwordChangeSuccess && (
                      <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">Password updated successfully.</span>
                      </div>
                    )}
                    {passwordChangeError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center space-x-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm">{passwordChangeError}</span>
                      </div>
                    )}
                    <form onSubmit={handlePasswordChange} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          New Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="input-field pl-10 pr-10"
                            placeholder="Enter new password"
                            required
                            minLength={8}
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <PasswordStrengthMeter password={newPassword} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <input
                            type={showConfirmNewPassword ? 'text' : 'password'}
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            className="input-field pl-10 pr-10"
                            placeholder="Confirm new password"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            {showConfirmNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        {confirmNewPassword.length > 0 && (
                          <p className={`text-xs mt-1.5 ${newPasswordsMatch ? 'text-green-600' : 'text-red-500'}`}>
                            {newPasswordsMatch ? 'Passwords match' : 'Passwords do not match'}
                          </p>
                        )}
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        disabled={passwordChangeLoading || !newPasswordValid || !newPasswordsMatch}
                        className="btn-primary text-sm flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {passwordChangeLoading ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Lock className="w-4 h-4" />
                        )}
                        <span>{passwordChangeLoading ? 'Updating...' : 'Update Password'}</span>
                      </motion.button>
                    </form>
                  </SettingCard>
                )}

                {/* Google OAuth users see info instead */}
                {!isEmailUser && (
                  <SettingCard
                    title="Password"
                    description="Your account uses Google sign-in"
                  >
                    <p className="text-sm text-gray-600">
                      Your account is linked to Google. Password management is handled through your Google account settings.
                    </p>
                  </SettingCard>
                )}

                <SettingCard
                  title="Account Security"
                  description="Manage your account security settings"
                >
                  <div className="space-y-4">
                    <Toggle
                      enabled={settings.twoFactorEnabled}
                      onChange={(value) => handleSettingChange('twoFactorEnabled', value)}
                      label="Two-Factor Authentication"
                      description="Add an extra layer of security to your account"
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Session Timeout (minutes)
                      </label>
                      <select
                        value={settings.sessionTimeout}
                        onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value))}
                        className="input-field"
                      >
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={60}>1 hour</option>
                        <option value={120}>2 hours</option>
                        <option value={480}>8 hours</option>
                      </select>
                    </div>
                  </div>
                </SettingCard>

                <SettingCard
                  title="API Keys"
                  description="Manage your API keys for integrations"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Production API Key</p>
                        <p className="text-xs text-gray-500">
                          {showApiKeys ? 'sk_live_1234567890abcdef' : '••••••••••••••••'}
                        </p>
                      </div>
                      <button
                        onClick={() => setShowApiKeys(!showApiKeys)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                      >
                        {showApiKeys ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <button className="btn-secondary text-sm">
                      Generate New API Key
                    </button>
                  </div>
                </SettingCard>
              </>
            )}

            {activeTab === 'data' && (
              <>
                <SettingCard
                  title="Data Export"
                  description="Download your CRM data"
                >
                  <div className="space-y-3">
                    <button className="btn-secondary text-sm w-full sm:w-auto">
                      Export All Leads (CSV)
                    </button>
                    <button className="btn-secondary text-sm w-full sm:w-auto">
                      Export Lead Events (CSV)
                    </button>
                    <button className="btn-secondary text-sm w-full sm:w-auto">
                      Export Complete Data (JSON)
                    </button>
                  </div>
                </SettingCard>

                <SettingCard
                  title="Data Retention"
                  description="Manage how long your data is stored"
                >
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Lead Data Retention
                      </label>
                      <select className="input-field">
                        <option value="forever">Keep forever</option>
                        <option value="2years">2 years</option>
                        <option value="1year">1 year</option>
                        <option value="6months">6 months</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Event Data Retention
                      </label>
                      <select className="input-field">
                        <option value="forever">Keep forever</option>
                        <option value="1year">1 year</option>
                        <option value="6months">6 months</option>
                        <option value="3months">3 months</option>
                      </select>
                    </div>
                  </div>
                </SettingCard>

                <SettingCard
                  title="Danger Zone"
                  description="Irreversible actions"
                >
                  <div className="space-y-3">
                    <button className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm">
                      Delete All Lead Data
                    </button>
                    <button className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm">
                      Delete Account
                    </button>
                  </div>
                </SettingCard>
              </>
            )}
          </motion.div>

          {/* Save Button */}
          <div className="mt-8 flex justify-end">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={isSaving}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{isSaving ? 'Saving...' : 'Save Changes'}</span>
            </motion.button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;